import { ConfirmModal, Input, Select, InputSizes, SelectSizes } from '@clockout-dev/design-elements';

/**
 * Shared create/edit modal for a role's name + description. Editing an
 * existing role reuses this exact component with `showCloneFrom={false}` —
 * cloning only makes sense when spawning a brand-new role, not when
 * changing an existing one's name/description.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} props.name
 * @param {string} [props.description]
 * @param {string|null} [props.cloneFromId]
 * @param {Array} [props.roles] - existing roles, offered as clone-from options (create mode only)
 * @param {boolean} [props.showCloneFrom=true] - false hides the "Start from" selector (edit mode)
 * @param {string} [props.title='New role']
 * @param {string} [props.helpText] - copy shown under the title
 * @param {string} [props.submitText] - okText while idle, defaults by mode
 * @param {string} [props.submittingText] - okText while submitting, defaults by mode
 * @param {boolean} props.submitting
 * @param {function} props.onNameChange
 * @param {function} [props.onDescriptionChange]
 * @param {function} [props.onCloneFromChange]
 * @param {function} props.onCancel
 * @param {function} props.onSubmit
 */
function NewRoleModal({
    open,
    name,
    description,
    cloneFromId,
    roles = [],
    showCloneFrom = true,
    title,
    helpText,
    submitText,
    submittingText,
    submitting,
    onNameChange,
    onDescriptionChange,
    onCloneFromChange,
    onCancel,
    onSubmit,
}) {
    const canSubmit = (name || '').trim().length > 0 && !submitting;
    const cloneOptions = roles.map((role) => ({ value: role.id, label: role.name }));

    const handleOk = async () => {
        if (canSubmit) await onSubmit();
        return false;
    };

    return (
        <ConfirmModal
            title={title ?? 'New role'}
            description={helpText ?? "Give it a name. You can start from an existing role's scopes, or start with none."}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            okText={submitting ? (submittingText ?? 'Saving...') : (submitText ?? 'Create role')}
            cancelText="Cancel"
            confirmLoading={submitting}
            okButtonProps={{ disabled: !canSubmit }}
            destroyOnHidden
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Input
                    size={InputSizes.SMALL}
                    id="roleName"
                    label="Role name"
                    placeholder="e.g. Auditor"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                />
                <Input.TextArea
                    size={InputSizes.SMALL}
                    id="roleDescription"
                    label="Description (optional)"
                    rows={2}
                    maxLength={512}
                    showCount
                    placeholder="What is this role for?"
                    value={description}
                    onChange={(e) => onDescriptionChange?.(e.target.value)}
                />
                {showCloneFrom ? (
                    <Select
                        size={SelectSizes.SMALL}
                        id="newRoleCloneFrom"
                        label="Start from (optional)"
                        placeholder="Start with no scopes"
                        options={cloneOptions}
                        value={cloneFromId}
                        onChange={(value) => onCloneFromChange(value)}
                        allowClear
                    />
                ) : null}
            </div>
        </ConfirmModal>
    );
}

export default NewRoleModal;
